import {
  Lead,
  LeadBiddingAnalysis,
  LeadBiddingAnalysisRequestOptions,
} from '../types';
import { auth } from './firebase';
import { BIDDING_AGENT_ENDPOINT } from './biddingAgentGuide';
import { logAgentActivityForLead } from './portalActivityLogger';

const BIDDING_ANALYSIS_POLL_TIMEOUT_MS = 10 * 60 * 1000;
const BIDDING_ANALYSIS_REQUESTS = 'leadBiddingAnalysisRequests';

interface BiddingAnalysisRequestOutcome {
  status: 'complete' | 'failed';
  error?: string;
}

async function getStaffIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error('Sign in required to run bidding analysis');
  }
  return user.getIdToken();
}

export async function requestBiddingAgentAnalysis(
  leadId: string,
  options?: LeadBiddingAnalysisRequestOptions,
): Promise<void> {
  const token = await getStaffIdToken();
  const response = await fetch(BIDDING_AGENT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      action: 'requestLeadBiddingAnalysis',
      leadId,
      dealId: options?.dealId,
      includeDocuments: options?.includeDocuments !== false,
    }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || `Failed to queue bidding analysis (HTTP ${response.status})`);
  }
}

export function waitForBiddingAgentAnalysis(
  leadId: string,
  timeoutMs = BIDDING_ANALYSIS_POLL_TIMEOUT_MS,
): Promise<BiddingAnalysisRequestOutcome> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;

    const finish = (outcome: BiddingAnalysisRequestOutcome) => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      clearTimeout(timer);
      resolve(outcome);
    };

    const fail = (err: Error) => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      clearTimeout(timer);
      reject(err);
    };

    const timer = setTimeout(() => {
      fail(new Error('Bidding agent analysis timed out — ensure the Mac mini agent is running'));
    }, timeoutMs);

    import('firebase/firestore')
      .then(({ doc, onSnapshot }) =>
        import('./firebase').then(({ db }) => {
          const ref = doc(db, BIDDING_ANALYSIS_REQUESTS, leadId);
          unsubscribe = onSnapshot(
            ref,
            snap => {
              if (!snap.exists()) return;
              const status = snap.data()?.status;
              if (status === 'complete') {
                finish({ status: 'complete' });
              } else if (status === 'failed') {
                finish({
                  status: 'failed',
                  error: (snap.data()?.error as string) || 'Bidding analysis failed',
                });
              }
            },
            err => fail(err instanceof Error ? err : new Error(String(err))),
          );
        }),
      )
      .catch(err => fail(err instanceof Error ? err : new Error(String(err))));
  });
}

function parseFirestoreDate(value: unknown): Date {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  const d = new Date(value as string);
  return isNaN(d.getTime()) ? new Date() : d;
}

function toDateOrNull(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }
  const d = new Date(value as string);
  return isNaN(d.getTime()) ? null : d;
}

export type BiddingRequestStatus = 'pending' | 'processing' | 'complete' | 'failed';

export interface BiddingAnalysisRequestSnapshot {
  status: BiddingRequestStatus;
  requestedAt: Date | null;
  startedAt: Date | null;
  completedAt: Date | null;
  error?: string;
}

/**
 * Live-subscribe to the background bidding-analysis request doc for a lead.
 * Drives the "running in the background" UI so it survives closing/reopening
 * the window: the Mac-mini agent processes the queued request independently.
 * Returns an unsubscribe function.
 */
export function subscribeBiddingAnalysisRequest(
  leadId: string,
  cb: (req: BiddingAnalysisRequestSnapshot | null) => void,
): () => void {
  let active = true;
  let unsubscribe: (() => void) | null = null;

  Promise.all([import('firebase/firestore'), import('./firebase')])
    .then(([{ doc, onSnapshot }, { db }]) => {
      if (!active) return;
      const ref = doc(db, BIDDING_ANALYSIS_REQUESTS, leadId);
      unsubscribe = onSnapshot(
        ref,
        snap => {
          if (!snap.exists()) {
            cb(null);
            return;
          }
          const d = snap.data() as Record<string, unknown>;
          cb({
            status: (d.status as BiddingRequestStatus) || 'pending',
            requestedAt: toDateOrNull(d.requestedAt),
            startedAt: toDateOrNull(d.startedAt),
            completedAt: toDateOrNull(d.completedAt),
            error: typeof d.error === 'string' ? d.error : undefined,
          });
        },
        () => cb(null),
      );
    })
    .catch(() => {});

  return () => {
    active = false;
    if (unsubscribe) unsubscribe();
  };
}

// ── Proposal Review (Mode 2) — queue keyed by dealId, written back to enhancedDeals/{dealId}.proposalReview ──

const PROPOSAL_REVIEW_REQUESTS = 'proposalReviewRequests';

export type ProposalReviewRequestStatus = BiddingRequestStatus;

export interface ProposalReviewRequestSnapshot {
  status: ProposalReviewRequestStatus;
  requestedAt: Date | null;
  startedAt: Date | null;
  completedAt: Date | null;
  error?: string;
}

/**
 * Enqueue a deal's proposal for background agent review (Firebase ID token auth).
 * Non-blocking — the Mac-mini agent polls, reviews, and writes back independently.
 * Throws on auth/validation errors (e.g. no proposal to review).
 */
export async function requestProposalReview(
  dealId: string,
  options?: { leadId?: string },
): Promise<void> {
  const token = await getStaffIdToken();
  const response = await fetch(BIDDING_AGENT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      action: 'requestProposalReview',
      dealId,
      leadId: options?.leadId,
    }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || `Failed to queue proposal review (HTTP ${response.status})`);
  }
}

/**
 * Live-subscribe to the background proposal-review request doc for a deal.
 * Drives the "Reviewing…" button state so it survives closing/reopening the
 * window. Returns an unsubscribe function.
 */
export function subscribeProposalReviewRequest(
  dealId: string,
  cb: (req: ProposalReviewRequestSnapshot | null) => void,
): () => void {
  let active = true;
  let unsubscribe: (() => void) | null = null;

  Promise.all([import('firebase/firestore'), import('./firebase')])
    .then(([{ doc, onSnapshot }, { db }]) => {
      if (!active) return;
      const ref = doc(db, PROPOSAL_REVIEW_REQUESTS, dealId);
      unsubscribe = onSnapshot(
        ref,
        snap => {
          if (!snap.exists()) {
            cb(null);
            return;
          }
          const d = snap.data() as Record<string, unknown>;
          cb({
            status: (d.status as ProposalReviewRequestStatus) || 'pending',
            requestedAt: toDateOrNull(d.requestedAt),
            startedAt: toDateOrNull(d.startedAt),
            completedAt: toDateOrNull(d.completedAt),
            error: typeof d.error === 'string' ? d.error : undefined,
          });
        },
        () => cb(null),
      );
    })
    .catch(() => {});

  return () => {
    active = false;
    if (unsubscribe) unsubscribe();
  };
}

function normalizeBiddingAnalysis(raw: Record<string, unknown>): LeadBiddingAnalysis {
  return {
    ...raw,
    analyzedAt: parseFirestoreDate(raw.analyzedAt),
    lastUpdated: parseFirestoreDate(raw.lastUpdated),
    status: (raw.status as LeadBiddingAnalysis['status']) || 'complete',
  } as LeadBiddingAnalysis;
}

export async function readServerBiddingAnalysis(leadId: string): Promise<LeadBiddingAnalysis | null> {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('./firebase');

    let docSnap = await getDoc(doc(db, 'leads', leadId));
    if (!docSnap.exists()) {
      docSnap = await getDoc(doc(db, 'testLeads', leadId));
    }
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    const raw = data?.biddingAnalysis;
    if (!raw || typeof raw !== 'object') return null;

    return normalizeBiddingAnalysis(raw as Record<string, unknown>);
  } catch {
    return null;
  }
}

export async function analyzeLeadForBidding(
  lead: Lead,
  options?: LeadBiddingAnalysisRequestOptions,
): Promise<LeadBiddingAnalysis> {
  if (!lead.id) {
    throw new Error('Lead ID is required for bidding analysis');
  }

  await requestBiddingAgentAnalysis(lead.id, options);

  const outcome = await waitForBiddingAgentAnalysis(lead.id);
  if (outcome.status === 'failed') {
    throw new Error(outcome.error || 'Bidding agent analysis failed');
  }

  const result = await readServerBiddingAnalysis(lead.id);
  if (!result) {
    throw new Error('Analysis completed but no data was written to the lead');
  }

  // Surface the agent run in the workspace activity feed (no-op without a portal).
  void logAgentActivityForLead(lead.id, 'bidding_analysis_ready', {
    itemCount: result.timberExtraction?.items?.length,
  });

  return result;
}

export function getQualificationColor(recommendation: string): string {
  switch (recommendation) {
    case 'go':
      return '#16a34a';
    case 'caution':
      return '#ca8a04';
    case 'no_go':
      return '#dc2626';
    default:
      return '#6b7280';
  }
}

export function getQualificationLabel(recommendation: string): string {
  switch (recommendation) {
    case 'go':
      return 'Go';
    case 'caution':
      return 'Caution';
    case 'no_go':
      return 'No-Go';
    default:
      // Humanize any agent-specific recommendation (e.g. "bid_timber_only").
      return recommendation
        ? recommendation.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : 'Reviewed';
  }
}

export function formatCurrency(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return `$${Math.round(value).toLocaleString()}`;
}
